// Core
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import { FieldSearchSelect, RemoteTable } from "components/AtomsV2";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetCoefficientsByCommunityIdPaginationQuery } from "state/coefficients/coefficientsApiSlice";

// Utils
import { useGetMeters } from "utils/crud/read";
import { datetimeToDate, datetimeToTime } from "utils/time";

const cpe = (v) => <span className="font-mono text-[12.5px] text-ink">{v}</span>;
const valueCell = (v) => (
  <span className="tabular-nums" style={{ color: "var(--accent)", fontWeight: 600 }}>
    {v != null ? Number(v).toFixed(4) : "—"}
  </span>
);

function ListCoefficients() {
  const community = useSelector(selectCurrentCommunity);
  const meters = useGetMeters("true");
  const { t } = useTranslation(["community"]);

  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [sortOrder, setSortOrder] = useState(undefined);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [meterId, setMeterId] = useState(null);

  const { data, isFetching } = useGetCoefficientsByCommunityIdPaginationQuery(
    {
      page,
      limit,
      date,
      time,
      meterId,
      communityId: community.id,
      sortColumn: sortOrder?.column,
      sortDirection: sortOrder?.direction,
    },
    {
      pollingInterval: 15000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    }
  );

  const meterIdToIdentifier = (id) => meters?.find((m) => m.id === id)?.identifier ?? "—";

  const rows = useMemo(
    () =>
      (data?.data ?? []).map((row, i) => ({
        id: `${row.datetime_timestamp}-${row.meter_id}-${i}`,
        date: datetimeToDate(row.datetime_timestamp),
        time: datetimeToTime(row.datetime_timestamp),
        meter_id: row.meter_id ? meterIdToIdentifier(row.meter_id) : "—",
        value: row.value,
      })),
    [data, meters]
  );

  const columns = [
    {
      key: "date",
      label: t("fixedCoefficients.table.columns.date"),
      sortable: true,
      width: "18%",
    },
    {
      key: "time",
      label: t("fixedCoefficients.table.columns.time"),
      sortable: true,
      width: "15%",
    },
    {
      key: "meter_id",
      label: t("fixedCoefficients.table.columns.meter"),
      render: cpe,
    },
    {
      key: "value",
      label: t("fixedCoefficients.table.columns.value"),
      align: "right",
      render: valueCell,
    },
  ];

  const allLabel = t("filters.all", { defaultValue: "Todos", ns: "common" });
  const dateOptions = [
    { value: "", label: allLabel },
    ...(data?.distinct_dates ?? []).map((o) => ({ value: o.date, label: o.date })),
  ];
  const timeOptions = [
    { value: "", label: allLabel },
    ...(data?.distinct_times ?? []).map((o) => ({ value: o.time, label: o.time })),
  ];
  const meterOptions = [
    { value: "", label: allLabel },
    ...(meters ?? []).map((m) => ({ value: m.identifier, label: m.identifier })),
  ];

  const resetPage = () => setPage(0);
  const onDate = (v) => {
    resetPage();
    setDate(v || null);
  };
  const onTime = (v) => {
    resetPage();
    setTime(v || null);
  };
  const onMeter = (identifier) => {
    resetPage();
    setMeterId(meters?.find((m) => m.identifier === identifier)?.id ?? null);
  };

  const filters = (
    <>
      <FieldSearchSelect
        label={t("fixedCoefficients.table.columns.date")}
        value={date ?? ""}
        onChange={onDate}
        options={dateOptions}
      />
      <FieldSearchSelect
        label={t("fixedCoefficients.table.columns.time")}
        value={time ?? ""}
        onChange={onTime}
        options={timeOptions}
      />
      <FieldSearchSelect
        label={t("fixedCoefficients.table.columns.meter")}
        value={meters?.find((m) => m.id === meterId)?.identifier ?? ""}
        onChange={onMeter}
        options={meterOptions}
      />
    </>
  );

  return (
    <div className="rec-card">
      <RemoteTable
        columns={columns}
        rows={rows}
        totalCount={data?.total_count ?? 0}
        page={page}
        limit={limit}
        onPageChange={setPage}
        sort={sortOrder}
        onSortChange={setSortOrder}
        filters={filters}
        loading={isFetching}
      />
    </div>
  );
}

export default ListCoefficients;
